import { useEffect, useMemo, useRef, useState } from "react";
import { Link2, Search, X } from "lucide-react";
import api from "../../../services/apiService";
import UserAvatar from "../../../components/ui/UserAvatar";

const accountName = (u) => [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email.split("@")[0];
const ROLE_LABEL = { admin: "Admin", hr: "HR", manager: "Manager", user: "Employee" };

// Picks the login (user account) linked to an HR employee. Only accounts not linked to another
// employee are offered; the server enforces the same rule.
function LinkedUserPicker({ value, onChange, employeeId }) {
  const [users, setUsers] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const boxRef = useRef(null);

  useEffect(() => {
    api
      .get("/hr/employees/linkable-users", { params: employeeId ? { employeeId } : {} })
      .then((res) => setUsers(Array.isArray(res.data) ? res.data : []))
      .catch(() => setLoadError("Couldn't load user accounts."));
  }, [employeeId]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => !boxRef.current?.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const selected = users.find((u) => String(u.id) === String(value)) || null;
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((u) => !q || accountName(u).toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
  }, [users, query]);

  const pick = (id) => {
    onChange(id);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={boxRef} className="relative" data-tour="hr-linked-account">
      {selected ? (
        <div className="flex items-center gap-3 rounded-lg border border-app-primary bg-app-input px-3 py-2">
          <UserAvatar name={accountName(selected)} src={selected.avatarThumbUrl} seed={selected.id} size="sm" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-app-primary">{accountName(selected)}</span>
            <span className="block truncate text-xs text-app-muted">{selected.email} · {ROLE_LABEL[selected.role] || selected.role}</span>
          </span>
          <button type="button" onClick={() => setOpen((v) => !v)} className="rounded-md px-2 py-1 text-xs font-semibold text-[#2470B7] hover:bg-app-hover dark:text-blue-400">Change</button>
          <button type="button" onClick={() => pick("")} className="rounded-md p-1 text-app-muted hover:bg-app-hover hover:text-app-primary" aria-label="Unlink user account" title="Unlink">
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center gap-2 rounded-lg border border-dashed border-app-primary bg-app-input px-3 py-2 text-left text-sm text-app-muted hover:border-[#2470B7] hover:text-app-primary"
        >
          <Link2 className="h-4 w-4" /> Link a user account (optional)
        </button>
      )}

      {open && (
        <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-lg border border-app-primary bg-app-card shadow-xl">
          <div className="relative border-b border-app-primary p-2">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-app-muted" />
            <input
              ref={(el) => el && el.focus()}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or email"
              aria-label="Search user accounts"
              className="w-full rounded-md bg-app-secondary py-1.5 pl-8 pr-2 text-sm text-app-primary outline-none"
            />
          </div>
          <ul className="max-h-56 overflow-y-auto py-1" role="listbox">
            {loadError && <li className="px-3 py-2 text-sm text-app-error">{loadError}</li>}
            {!loadError && matches.length === 0 && (
              <li className="px-3 py-2 text-sm text-app-muted">No unlinked accounts match. Each account can be linked to one employee.</li>
            )}
            {matches.map((u) => (
              <li key={u.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={String(u.id) === String(value)}
                  onClick={() => pick(u.id)}
                  className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-app-hover"
                >
                  <UserAvatar name={accountName(u)} src={u.avatarThumbUrl} seed={u.id} size="sm" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-app-primary">{accountName(u)}</span>
                    <span className="block truncate text-xs text-app-muted">{u.email}</span>
                  </span>
                  <span className="text-xs text-app-muted">{ROLE_LABEL[u.role] || u.role}{u.isActive === false ? " · inactive" : ""}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="mt-1 text-xs text-app-muted">
        The linked person signs in with this account; their profile, chat name and photo use this employee record.
      </p>
    </div>
  );
}

export default LinkedUserPicker;
